import api from "./base-api"

// Proxy to the ai-zendesk Dialog Approval API (backend: /api/v1/ai-zendesk/dialogs).
// ai-zendesk has a single (production) environment, so these calls ignore X-Target-Environment.
const aiZendeskApi = api.injectEndpoints({
    endpoints: build => ({
        getAiDialogs: build.query({
            // game: ai-zendesk game key (clockmaker, toh, ...); omitted -> all games
            query: ({hasPendingAnswers = false, game} = {}) => ({
                url: "v1/ai-zendesk/dialogs",
                params: game ? {hasPendingAnswers, game} : {hasPendingAnswers}
            }),
            providesTags: ["AiDialogs"]
        }),
        getAiDialog: build.query({
            query: (ticketId) => ({url: `v1/ai-zendesk/dialogs/${ticketId}`}),
            providesTags: (result, error, ticketId) => [{type: "AiDialog", id: ticketId}]
        }),
        // send=false: mark approved only (status APPROVED, nothing goes to the player);
        // send=true: approve and deliver (status SENT). Works for PENDING_APPROVAL and, with send, for APPROVED.
        approveAiAnswer: build.mutation({
            query: ({answerId, send = false}) => ({
                url: `v1/ai-zendesk/dialogs/answers/${answerId}/approve`,
                method: "POST",
                body: {send}
            }),
            invalidatesTags: (result, error, {ticketId}) => ["AiDialogs", {type: "AiDialog", id: ticketId}]
        }),
        rejectAiAnswer: build.mutation({
            query: ({answerId, reason}) => ({
                url: `v1/ai-zendesk/dialogs/answers/${answerId}/reject`,
                method: "POST",
                body: {reason: reason || null}
            }),
            invalidatesTags: (result, error, {ticketId}) => ["AiDialogs", {type: "AiDialog", id: ticketId}]
        }),
        // debug: delete every unsent draft of the ticket (SENT answers are kept) -> {ticketId, deleted}
        deleteAiTicketDrafts: build.mutation({
            query: ({ticketId}) => ({url: `v1/ai-zendesk/dialogs/${ticketId}/answers`, method: "DELETE"}),
            invalidatesTags: (result, error, {ticketId}) => ["AiDialogs", {type: "AiDialog", id: ticketId}]
        }),
        // debug: delete unsent drafts for one player message (messageId = message.id) -> {ticketId, deleted}
        deleteAiMessageDrafts: build.mutation({
            query: ({messageId}) => ({url: `v1/ai-zendesk/dialogs/messages/${messageId}/answers`, method: "DELETE"}),
            invalidatesTags: (result, error, {ticketId}) => ["AiDialogs", {type: "AiDialog", id: ticketId}]
        }),
        // debug: drop unsent drafts for the message and generate the answer again (synchronous, up to ~1 min)
        regenerateAiAnswer: build.mutation({
            query: ({messageId}) => ({url: `v1/ai-zendesk/dialogs/messages/${messageId}/regenerate`, method: "POST"}),
            invalidatesTags: (result, error, {ticketId}) => ["AiDialogs", {type: "AiDialog", id: ticketId}]
        }),
        // knowledge-base candidates the quality judge extracted from this ticket (operator reply -> general game rule)
        getAiTicketKnowledgeCandidates: build.query({
            query: (ticketId) => ({url: `v1/ai-zendesk/dialogs/${ticketId}/knowledge-candidates`}),
            providesTags: ["AiKnowledge"]
        })
    })
})

export const {
    useGetAiDialogsQuery,
    useGetAiDialogQuery,
    useApproveAiAnswerMutation,
    useRejectAiAnswerMutation,
    useDeleteAiTicketDraftsMutation,
    useDeleteAiMessageDraftsMutation,
    useRegenerateAiAnswerMutation,
    useGetAiTicketKnowledgeCandidatesQuery
} = aiZendeskApi

export default aiZendeskApi
